import React, { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { imgProxy } from "../lib/imgProxy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ContactForm({ mode = "contact" }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", property_type: "", property_location: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, type: mode });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", property_type: "", property_location: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full px-4 py-3 bg-white border border-[hsl(var(--line))] rounded-lg focus:outline-none focus:border-[hsl(var(--gold))] focus:ring-1 focus:ring-[hsl(var(--gold))] text-sm transition";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`form-${mode}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder={t.contact.fName} className={field} data-testid={`${mode}-name`} />
        <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder={t.contact.fEmail} className={field} data-testid={`${mode}-email`} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder={t.contact.fPhone} className={field} data-testid={`${mode}-phone`} />
        {mode === "contact" ? (
          <input value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder={t.contact.fSubject} className={field} data-testid="contact-subject" />
        ) : (
          <input value={form.property_type} onChange={(e) => update("property_type", e.target.value)} placeholder={t.contact.fType} className={field} data-testid="owner-type" />
        )}
      </div>
      {mode === "owner" && (
        <input value={form.property_location} onChange={(e) => update("property_location", e.target.value)} placeholder={t.contact.fLocation} className={field} data-testid="owner-location" />
      )}
      <textarea required rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder={t.contact.fMessage} className={`${field} resize-none`} data-testid={`${mode}-message`} />
      <button type="submit" disabled={loading} className="btn-gold w-full sm:w-auto px-8 py-3.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60" data-testid={`${mode}-submit`}>
        <Send className="w-4 h-4" /> {loading ? t.contact.sending : t.contact.send}
      </button>
    </form>
  );
}

export default function ContactPage({ mode = "contact" }) {
  const { t } = useI18n();
  const isOwner = mode === "owner";

  return (
    <main>
      <section className="relative h-[42vh] min-h-[320px] flex items-center hero-bg">
        <div className="hero-img" style={{ backgroundImage: isOwner
          ? `url('${imgProxy("https://customer-assets.emergentagent.com/job_chamonix-locations/artifacts/1zflew1z_IMG-20251024-WA0000.jpg", { w: 2000, q: 78 })}')`
          : `url('${imgProxy("https://customer-assets.emergentagent.com/job_chamonix-locations/artifacts/r4vyra0d_Chamonix_img.jpg", { w: 2000, q: 78 })}')` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 text-white">
          <p className="text-[11px] uppercase tracking-[.3em] opacity-90 mb-4">MyHost</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-tight" data-testid={`${mode}-title`}>
            {isOwner ? t.contact.rentTitle : t.contact.title}
          </h1>
          <p className="mt-4 opacity-90 max-w-xl">{isOwner ? t.contact.rentSubtitle : t.contact.subtitle}</p>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3">
          {isOwner && (
            <p className="font-serif text-xl italic text-[hsl(var(--ink-soft))] mb-8 leading-relaxed">« {t.contact.rentIntro} »</p>
          )}
          <ContactForm mode={mode} />
        </div>

        <aside className="lg:col-span-2 space-y-6">
          <div className="p-8 bg-[hsl(var(--bg-warm))] rounded-xl border border-[hsl(var(--line))]">
            <h3 className="font-serif text-2xl mb-6">{t.contact.info}</h3>
            <ul className="space-y-5 text-sm">
              <li className="flex gap-4">
                <Phone className="w-5 h-5 text-[hsl(var(--gold-deep))] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-ink))] mb-1">{t.contact.phone}</div>
                  <a href="tel:+33614488635" className="font-medium">+33 6 14 48 86 35</a>
                </div>
              </li>
              <li className="flex gap-4">
                <Mail className="w-5 h-5 text-[hsl(var(--gold-deep))] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-ink))] mb-1">{t.contact.email}</div>
                  <a href="mailto:myhost.claudiu@gmail.com" className="font-medium break-all">myhost.claudiu@gmail.com</a>
                </div>
              </li>
              <li className="flex gap-4">
                <MapPin className="w-5 h-5 text-[hsl(var(--gold-deep))] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-ink))] mb-1">{t.contact.location}</div>
                  <div className="font-medium">Chamonix Mont-Blanc, France</div>
                </div>
              </li>
            </ul>
          </div>
        </aside>
      </section>
    </main>
  );
}
